import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TextPlaceholderScenarioEntry } from './text-placeholder-api';
import {
  findPlaceholderTrigger,
  getCaretCoordinates,
} from '../../utils/text-placeholders';

export interface PlaceholderAutocompleteState {
  open: boolean;
  query: string;
  startIndex: number;
  selectedIndex: number;
  position: { top: number; left: number } | null;
  filteredEntries: TextPlaceholderScenarioEntry[];
  showCreate: boolean;
}

const INITIAL_STATE: PlaceholderAutocompleteState = {
  open: false,
  query: '',
  startIndex: 0,
  selectedIndex: 0,
  position: null,
  filteredEntries: [],
  showCreate: false,
};

function filterEntries(
  entries: TextPlaceholderScenarioEntry[],
  query: string
): TextPlaceholderScenarioEntry[] {
  const q = query.toLowerCase();
  if (!q) return entries;
  return entries.filter(
    (entry) =>
      entry.key.toLowerCase().includes(q) ||
      (entry.label?.toLowerCase().includes(q) ?? false)
  );
}

export function useTextPlaceholderAutocomplete(
  textareaRef: React.RefObject<HTMLTextAreaElement | null>,
  value: string,
  entries: TextPlaceholderScenarioEntry[],
  enabled: boolean
) {
  const [state, setState] = useState<PlaceholderAutocompleteState>(INITIAL_STATE);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createQuery, setCreateQuery] = useState('');

  const updateFromTextarea = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea || !enabled) {
      setState(INITIAL_STATE);
      return;
    }

    const trigger = findPlaceholderTrigger(value, textarea.selectionStart);
    if (!trigger) {
      setState(INITIAL_STATE);
      return;
    }

    const filteredEntries = filterEntries(entries, trigger.query);
    const showCreate =
      trigger.query.length > 0 &&
      !entries.some((entry) => entry.key.toLowerCase() === trigger.query.toLowerCase());
    const itemCount = filteredEntries.length + (showCreate ? 1 : 0);

    setState((prev) => {
      const selectedIndex = Math.min(prev.selectedIndex, Math.max(0, itemCount - 1));
      return {
        open: true,
        query: trigger.query,
        startIndex: trigger.startIndex,
        selectedIndex,
        position: getCaretCoordinates(textarea, trigger.startIndex + 1 + trigger.query.length),
        filteredEntries,
        showCreate,
      };
    });
  }, [textareaRef, value, entries, enabled]);

  useEffect(() => {
    updateFromTextarea();
  }, [updateFromTextarea]);

  const close = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  const setSelectedIndex = useCallback((index: number) => {
    setState((prev) => ({ ...prev, selectedIndex: index }));
  }, []);

  const insertKey = useCallback(
    (key: string): string => {
      const textarea = textareaRef.current;
      if (!textarea) return value;

      const before = value.slice(0, state.startIndex);
      const after = value.slice(textarea.selectionStart);
      const insertion = `@${key}`;
      const nextValue = `${before}${insertion}${after}`;
      const nextCursor = before.length + insertion.length;

      window.requestAnimationFrame(() => {
        textarea.focus();
        textarea.setSelectionRange(nextCursor, nextCursor);
      });

      close();
      return nextValue;
    },
    [textareaRef, value, state.startIndex, close]
  );

  const openCreateModal = useCallback(() => {
    setCreateQuery(state.query);
    setCreateModalOpen(true);
    close();
  }, [state.query, close]);

  const totalItems = useMemo(
    () => state.filteredEntries.length + (state.showCreate ? 1 : 0),
    [state.filteredEntries.length, state.showCreate]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>): boolean => {
      if (!state.open || totalItems === 0) return false;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setState((prev) => ({
          ...prev,
          selectedIndex: Math.min(prev.selectedIndex + 1, totalItems - 1),
        }));
        return true;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setState((prev) => ({
          ...prev,
          selectedIndex: Math.max(prev.selectedIndex - 1, 0),
        }));
        return true;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return true;
      }

      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        return true;
      }

      return false;
    },
    [state, totalItems, close, insertKey, openCreateModal]
  );

  return {
    state,
    createModalOpen,
    createQuery,
    setCreateModalOpen,
    updateFromTextarea,
    insertKey,
    openCreateModal,
    handleKeyDown,
    setSelectedIndex,
    close,
  };
}
